import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Download, Upload, FileWarning, CheckCircle2 } from 'lucide-react';
import { Field, fmtDate } from './design';

// After a bulk file is built: download it (in parts over 10MB, part 1 first), record what DEX said
// about the upload, read DEX's error file back into a fix list, and build a file of only the records
// that failed once they are fixed.

const STATUS = { ok: 'Accepted in full', partial: 'Accepted, some records refused', failed: 'Refused' };
// What each line of DEX's error file is about, in words (never the kind value as it is stored).
const KIND = { client: 'Participant', case: 'Case', session: 'Session', unknown: 'Not one of ours' };
const isoToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

export default function DexUpload({ extract, onDownload, onNewFile }) {
  const [status, setStatus] = useState({ status: extract.uploadStatus || '', uploadedOn: isoToday(), note: extract.uploadNote || '' });
  // What was last recorded, kept here so the line under the form changes as soon as Record succeeds
  // (it used to show the result from when the page loaded until the page was reloaded).
  const [recorded, setRecorded] = useState(extract.uploadedOn ? { status: extract.uploadStatus, on: extract.uploadedOn, note: extract.uploadNote || '' } : null);
  const [errors, setErrors] = useState([]);
  const fileRef = useRef(null);
  const parts = Number(extract.parts || extract.excluded?.parts || 1);
  const loadErrors = useCallback(() => {
    api.get(`/aged-care/dex/extracts/${extract.id}/errors`).then((r) => setErrors(r.data || [])).catch(() => setErrors([]));
  }, [extract.id]);
  useEffect(() => { loadErrors(); }, [loadErrors]);
  const saveResult = async () => {
    try {
      await api.post(`/aged-care/dex/extracts/${extract.id}/result`, status);
      setRecorded({ status: status.status, on: status.uploadedOn || isoToday(), note: status.note });
      toast.success('Upload result recorded');
      // Accepted in full closes the errors of the file this one retried; show that straight away.
      loadErrors();
    }
    catch (e) { toast.error(e.response?.data?.message || 'Could not save'); }
  };
  const readErrors = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post(`/aged-care/dex/extracts/${extract.id}/errors`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${r.data.rows} row${r.data.rows === 1 ? '' : 's'} read, ${r.data.matched} tied to a record`); loadErrors();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not read that file'); }
    finally { if (fileRef.current) fileRef.current.value = ''; }
  };
  const fixed = async (row) => {
    try { await api.post(`/aged-care/dex/upload-errors/${row.id}/fixed`); toast.success('Marked fixed'); loadErrors(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not save'); }
  };
  const failedOnly = async () => {
    try { const r = await api.post(`/aged-care/dex/extracts/${extract.id}/failed-file`); toast.success(`File of the failed records built: ${r.data.recordCount} client${r.data.recordCount === 1 ? '' : 's'}, ${r.data.sessionCount} session${r.data.sessionCount === 1 ? '' : 's'}`); onNewFile(r.data); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not build the file'); }
  };
  const open = errors.filter((e) => !e.resolved_at);
  return (
    <div className="space-y-2">
      {parts > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[12.5px] text-slate-600">Over DEX's 10MB limit: upload the parts in order.</span>
          {Array.from({ length: parts }, (_, i) => <button key={i} type="button" className="btn-secondary btn-sm" onClick={() => onDownload(extract, i + 1)}><Download size={13} /> Part {i + 1}</button>)}
        </div>
      )}
      <div className="flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 p-2">
        <Field label="What DEX said about the upload">
          {(id) => (
            <select id={id} className="input-field !w-auto" value={status.status} onChange={(e) => setStatus((p) => ({ ...p, status: e.target.value }))}>
              <option value="">Not uploaded yet</option>{Object.entries(STATUS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          )}
        </Field>
        <Field label="Uploaded on">{(id) => <input id={id} type="date" className="input-field !w-auto" value={status.uploadedOn} onChange={(e) => setStatus((p) => ({ ...p, uploadedOn: e.target.value }))} />}</Field>
        {/* The note was always sent with the result but there was nowhere to type it. */}
        <Field label="Note" className="flex-1 min-w-[12rem]">
          {(id) => <input id={id} className="input-field" value={status.note} onChange={(e) => setStatus((p) => ({ ...p, note: e.target.value }))} placeholder="For example, upload ID or what the report said" maxLength={1000} />}
        </Field>
        <button type="button" className="btn-secondary btn-sm mb-0.5" disabled={!status.status} onClick={saveResult}><CheckCircle2 size={13} /> Record</button>
        <input ref={fileRef} type="file" accept=".csv,text/csv,text/plain" className="hidden" onChange={(e) => readErrors(e.target.files?.[0])} />
        <button type="button" className="btn-ghost btn-sm mb-0.5" onClick={() => fileRef.current?.click()}><Upload size={13} /> Read DEX's error file</button>
      </div>
      {open.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50/40 p-2 space-y-1">
          <p className="text-[12.5px] font-medium text-red-800 flex items-center gap-1.5"><FileWarning size={13} /> {open.length} record{open.length === 1 ? '' : 's'} to fix</p>
          <ul className="text-[12.5px] space-y-0.5">
            {open.map((e) => (
              <li key={e.id} className="flex flex-wrap items-baseline justify-between gap-2">
                <span className={e.severity === 'warning' ? 'text-amber-800' : 'text-red-800'}>{KIND[e.kind] || 'Record'}{e.name ? ` (${e.name})` : ''}: {e.message}</span>
                <button type="button" className="btn-ghost btn-sm" onClick={() => fixed(e)}>Mark fixed</button>
              </li>
            ))}
          </ul>
          <div className="flex justify-end"><button type="button" className="btn-primary btn-sm" onClick={failedOnly}>Build a file of only the failed records</button></div>
          <p className="text-[11.5px] text-slate-500">Fix each in Rostery first: the new file is built from today's details, for the same dates.</p>
        </div>
      )}
      {recorded && recorded.status && (
        <p className="text-[12px] text-slate-500">
          Last recorded: {STATUS[recorded.status] || 'Upload result'} on {fmtDate(recorded.on)}{recorded.note ? `. ${recorded.note}` : ''}
        </p>
      )}
    </div>
  );
}
